import { Building2, Check, Crown } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import useSWR from 'swr'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { apiFetcher } from '@/data/fetcher'
import { Settings } from '@/interfaces/Settings'
import API from '@/lib/api'

import { Tooltip, TooltipContent, TooltipTrigger } from './ui/tooltip'

export function AccountSwitch() {
  const [isLoading, setIsLoading] = useState(false)

  const { data: settings, mutate } = useSWR<Settings>('/api/v2/me', apiFetcher)

  // Don't render if no accounts are available
  if (!settings?.accounts || settings.accounts.length === 0) {
    return null
  }

  const handleAccountSwitch = async (accountId: string) => {
    setIsLoading(true)
    try {
      await API.post('/api/v2/me/account', { accountId })
      // Mutate settings to refresh the data
      await mutate()
      toast.success('Účet byl úspěšně přepnut')
      // Reload the page to refresh all data
      window.location.reload()
    } catch (error) {
      console.error('Failed to switch account:', error)
      toast.error('Nepodařilo se přepnout účet')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative rounded-full"
          disabled={isLoading}
        >
          <Building2 className="size-5" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-56">
        <DropdownMenuLabel>Přepnout účet</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {settings.accounts.map((account) => (
          <DropdownMenuItem
            key={account.id}
            onClick={() => handleAccountSwitch(account.id)}
            disabled={account.id === settings.currentAccountId}
            className="cursor-pointer"
          >
            <div className="flex items-center justify-between w-full">
              <div className="flex items-center gap-2">
                <span>{account.name}</span>
                <div className="flex flex-row gap-1">
                  {account.isPrimary && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Crown className="size-4" />
                      </TooltipTrigger>
                      <TooltipContent>Hlavní účet</TooltipContent>
                    </Tooltip>
                  )}
                  {account.id === settings.currentAccountId && (
                    <Check className="size-4" />
                  )}
                </div>
              </div>
            </div>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
